/**
 * FTUE store — tracks whether the first-time user experience should be shown.
 *
 * Completion is persisted via the storage abstraction so it survives restarts.
 *
 * Reset methods:
 *   - JS: useFTUEStore.getState().reset()
 *   - Android adb: adb shell run-as com.amazon.archimedes rm -rf databases/ftue
 *   - Dev menu: kebab menu → "Show FTUE"
 */

import { create } from "zustand";
import { registerDb, getById, put, remove } from "../lib/storage";

const DB_NAME = "ftue";
const STORE_NAME = "state";
registerDb(DB_NAME, [STORE_NAME]);

interface FTUEState {
  needsFTUE: boolean;
  isLoaded: boolean;

  load: () => Promise<void>;
  complete: () => void;
  reset: () => void;
}

export const useFTUEStore = create<FTUEState>((set) => ({
  needsFTUE: true,
  isLoaded: false,

  load: async () => {
    try {
      const record = await getById<{ id: string; completed: boolean }>(DB_NAME, STORE_NAME, "ftue");
      set({ needsFTUE: !record?.completed, isLoaded: true });
    } catch {
      set({ isLoaded: true });
    }
  },

  complete: () => {
    set({ needsFTUE: false });
    put(DB_NAME, STORE_NAME, { id: "ftue", completed: true }).catch(() => {});
  },

  reset: () => {
    set({ needsFTUE: true });
    remove(DB_NAME, STORE_NAME, "ftue").catch(() => {});
  },
}));
